import React from 'react';
import PropTypes from 'prop-types';
import { FocusScope } from '@react-aria/focus';
import { useEditor, useEditingCond, useNode } from '@splashthat/splash-cms-engine';
import Close from 'common/icons/CloseForm';

import {
    BRAND_LIBRARY_LIGHT_TEXT_COLOR,
    BRAND_LIBRARY_BUTTON_DEFAULT_COLOR,
    BRAND_LIBRARY_BUTTON_HOVER_COLOR,
    BRAND_LIBRARY_DARK_TEXT_COLOR,
    BRAND_LIBRARY_HEADLINE_TEXT,
    BRAND_LIBRARY_SUBHEADLINE_TEXT,
    BRAND_LIBRARY_BODY_TEXT,
} from 'common/constants/brandLibrary';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { getCustomCssClassName } from 'common/components/TouchpointEditor/tools/CustomCssTools/CustomCssTools';

import { Modal } from 'common/components/TouchpointEditor/contexts/ModalManager';

import {
    SpeakerModalContainer,
    SpeakerModalWrapper,
    CloseButtonContainer,
    SpeakerImage,
    SpeakerName,
    SpeakerTitleAndCompany,
    SpeakerBio,
    SpeakerModalGlobalStyles,
} from './SpeakerModal.styles';

const BASE_CLASS_NAME = 'SessionSpeakerModal';

const placeholderSpeaker = {
    id: '1',
    firstName: 'Speaker',
    lastName: 'Name',
    jobTitle: 'Speaker Title',
    company: 'Speaker Company',
    avatarUrl: 'https://d24wuq6o951i2g.cloudfront.net/images/avatar.png',
    bio: 'This is a placeholder speaker bio.',
};

export default function SpeakerModal({
    descendants: { modal, closeButton, speakerName, speakerTitleCompany, speakerBio },
    hovered,
    selected,
    id,
}) {
    const { theme } = useTouchpointData();
    const editor = useEditor((state) => ({ editing: state.editing }));
    const onCloseCallbackIfOnEditorMode = useEditingCond(() => editor.actions.unselectAllNodes());

    const node = useNode();
    const customCssClassName = getCustomCssClassName(node);

    return (
        <Modal modalId={id}>
            {({ isOpen, onResolve, speaker = placeholderSpeaker }) => {
                const shouldShowModal = isOpen || selected;

                const { firstName = '', lastName = '', bio, jobTitle, company, avatarUrl } = speaker;

                const shouldShowSpeakerBio = Boolean(bio);

                const shouldShowSpeakerName = firstName || lastName;
                const fullName = [firstName, lastName].filter(Boolean).join(' ');

                const shouldShowTitleAndCompanyInformation = jobTitle || company;
                const titleAndCompany = [jobTitle, company].filter(Boolean).join(', ');

                const onClose = onCloseCallbackIfOnEditorMode || onResolve;
                const preventModalClickFromClosing = (e) => e.stopPropagation();

                if (!shouldShowModal) {
                    return null;
                }

                return (
                    <SpeakerModalContainer className={BASE_CLASS_NAME} onClick={onClose}>
                        <SpeakerModalGlobalStyles isEditing={editor.editing} />
                        <FocusScope contain={!editor.editing} restoreFocus autoFocus>
                            <SpeakerModalWrapper
                                data-qa={`${BASE_CLASS_NAME}__modal-wrapper`}
                                $backgroundColor={theme.colors[modal.styles.backgroundColor]}
                                role="dialog"
                                aria-modal="true"
                                onClick={preventModalClickFromClosing}
                                className={customCssClassName}
                            >
                                <CloseButtonContainer
                                    $color={theme.colors[closeButton.styles.color]}
                                    $colorHover={theme.colors[closeButton.styles.colorHover]}
                                    onClick={onClose}
                                    data-qa={BASE_CLASS_NAME}
                                    aria-label="Close"
                                >
                                    <Close />
                                </CloseButtonContainer>

                                {avatarUrl && <SpeakerImage src={avatarUrl} alt={speakerName} />}

                                {shouldShowSpeakerName && (
                                    <SpeakerName
                                        $fontSize={speakerName.styles.fontSize}
                                        $fontColor={theme.colors[speakerName.styles.fontColor]}
                                        $fontFamily={theme.activeFonts[speakerName.styles.fontFamily]}
                                    >
                                        {fullName}
                                    </SpeakerName>
                                )}

                                {shouldShowTitleAndCompanyInformation && (
                                    <SpeakerTitleAndCompany
                                        $fontSize={speakerTitleCompany.styles.fontSize}
                                        $fontColor={theme.colors[speakerTitleCompany.styles.fontColor]}
                                        $fontFamily={theme.activeFonts[speakerTitleCompany.styles.fontFamily]}
                                    >
                                        {titleAndCompany}
                                    </SpeakerTitleAndCompany>
                                )}

                                {shouldShowSpeakerBio && (
                                    <SpeakerBio
                                        $fontSize={speakerBio.styles.fontSize}
                                        $fontColor={theme.colors[speakerBio.styles.fontColor]}
                                        $fontFamily={theme.activeFonts[speakerBio.styles.fontFamily]}
                                    >
                                        {bio}
                                    </SpeakerBio>
                                )}
                            </SpeakerModalWrapper>
                        </FocusScope>
                    </SpeakerModalContainer>
                );
            }}
        </Modal>
    );
}

SpeakerModal.splash = {
    related: {
        settings: React.lazy(() => import('./SpeakerModal.tools')),
    },
    defaultProps: {
        descendants: {
            modal: {
                styles: {
                    backgroundColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
                },
            },
            closeButton: {
                styles: {
                    color: BRAND_LIBRARY_BUTTON_DEFAULT_COLOR,
                    colorHover: BRAND_LIBRARY_BUTTON_HOVER_COLOR,
                },
            },
            speakerName: {
                styles: {
                    fontColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
                    fontFamily: BRAND_LIBRARY_HEADLINE_TEXT,
                    fontSize: 35,
                },
            },
            speakerTitleCompany: {
                styles: {
                    fontColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
                    fontFamily: BRAND_LIBRARY_SUBHEADLINE_TEXT,
                    fontSize: 24,
                },
            },
            speakerBio: {
                styles: {
                    fontColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
                    fontFamily: BRAND_LIBRARY_BODY_TEXT,
                    fontSize: 20,
                },
            },
        },
    },
};

SpeakerModal.propTypes = {
    descendants: PropTypes.object.isRequired,
    hovered: PropTypes.bool.isRequired,
    selected: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
};
